import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

export type CoachLesson = {
  id: string;
  coach_id: string;
  team_id: string | null;
  title: string;
  description: string | null;
  sport: string;
  created_at: string;
};

/** A coach-authored lesson is referenced everywhere by this stable id. */
export const customLessonId = (id: string) => `custom:${id}`;
export const isCustomLessonId = (lessonId: string) => lessonId.startsWith('custom:');
export const customLessonUuid = (lessonId: string) => lessonId.slice('custom:'.length);

/**
 * Lessons the coach wrote themselves. A lesson is valid with nothing but a
 * title — content (videos / articles / resources) is always optional.
 */
export function useCoachLessons(coachId: string | null | undefined) {
  const [lessons, setLessons] = useState<CoachLesson[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!coachId) {
      setLessons([]);
      setLoading(false);
      return;
    }
    const { data } = await supabase
      .from('coach_lessons' as any)
      .select('id, coach_id, team_id, title, description, sport, created_at')
      .eq('coach_id', coachId)
      .order('created_at', { ascending: false });
    setLessons(((data as any[]) ?? []) as CoachLesson[]);
    setLoading(false);
  }, [coachId]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  const createLesson = async (input: {
    title: string;
    description?: string;
    sport: string;
    teamId?: string | null;
  }) => {
    if (!coachId) return null;
    const { data, error } = await supabase
      .from('coach_lessons' as any)
      .insert({
        coach_id: coachId,
        team_id: input.teamId ?? null,
        title: input.title.trim(),
        description: input.description?.trim() || null,
        sport: input.sport,
      })
      .select('id, coach_id, team_id, title, description, sport, created_at')
      .single();
    if (error) throw error;
    await load();
    return data as unknown as CoachLesson;
  };

  const updateLesson = async (id: string, patch: Partial<Pick<CoachLesson, 'title' | 'description'>>) => {
    const { error } = await supabase.from('coach_lessons' as any).update(patch).eq('id', id);
    if (error) throw error;
    await load();
  };

  const deleteLesson = async (id: string) => {
    const { error } = await supabase.from('coach_lessons' as any).delete().eq('id', id);
    if (error) throw error;
    await supabase.from('lesson_assignments').delete().eq('lesson_id', customLessonId(id));
    await load();
  };

  return { lessons, loading, reload: load, createLesson, updateLesson, deleteLesson };
}
